/** Whether this deployment can produce a PDF of a filing - the Reports screen reads it **before**
 * it offers a link (S24, ADR-0078).
 *
 * ADR-0078 shipped a PDF link that answers a named `503 pdf_not_configured` until a renderer is
 * deployed. A control that predictably fails is the defect UX-04 names, so the screen asks first
 * and renders one of two things: the link, or the server's sentence saying PDF export is not
 * available on this deployment. It never discovers the answer by *firing the request and catching
 * the 503* - that fails once per reader, quietly, and puts a 503 in the logs for a page view.
 *
 * **Typed by hand, for now.** `GET /api/v1/artifacts/pdf-capability` is newer than the generated
 * `schema.d.ts` (the owner regenerates once, at integration), so `api.GET(...)` would not compile.
 * `apiFetch` is used and the body is **checked at the boundary rather than cast**: a body that is
 * not this shape is a `TypeError`, which the screen renders as "could not be checked" - never as
 * either answer, because guessing which is how a reader gets a link that fails or is told a feature
 * is missing that is not.
 *
 * **The tripwire below stops this being permanent.** When `make api-types` adds the route the
 * constant stops compiling, and whoever regenerates is sent here to delete the hand-written shape
 * and call `api.GET`.
 */

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "./client";
import { refusalFrom } from "./refusal";

export interface PdfExportCapability {
  /** A renderer is configured. Configured, not proven: a render can still fail, by name. */
  available: boolean;
  /** The server's sentence for `available: false`, printed as it stands (one home for the words,
   * so the screen cannot say something truer-sounding than the server knows). `null` when true. */
  message: string | null;
}

export const PDF_CAPABILITY_PATH = "/api/v1/artifacts/pdf-capability" as const;


export const pdfCapabilityKey = ["artifacts", "pdf-capability"] as const;

/** Exported for the one test that feeds it shapes a server should never send. */
export function readCapability(body: unknown): PdfExportCapability {
  if (body && typeof body === "object" && "available" in body && "message" in body) {
    const { available, message } = body;
    // A "not available" with no sentence is not an answer a screen can print, and "available" with
    // a sentence is a contradiction; both are refused rather than rendered as either.
    if (available === true && message === null) return { available, message };
    if (available === false && typeof message === "string" && message.trim() !== "") {
      return { available, message };
    }
  }
  throw new TypeError("The PDF export check's response could not be read.");
}

/** Read once per ten minutes at most: it is a property of the deployment, not of a filing, and a
 * screen that re-read it on every focus would be asking a question with one answer. `enabled` is
 * the caller's: there is nothing to ask about until a filing exists to print. */
export function usePdfCapability(enabled: boolean) {
  return useQuery({
    queryKey: pdfCapabilityKey,
    enabled,
    retry: false,
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PdfExportCapability> => {
      const response = await apiFetch(PDF_CAPABILITY_PATH);
      const body: unknown = await response.json().catch(() => null);
      // Thrown, not defaulted: `available ?? false` would tell a reader a feature is missing on the
      // strength of a dropped request.
      if (!response.ok) throw refusalFrom(response, body, "Whether PDF export is available could not be checked.");
      return readCapability(body);
    },
  });
}
